import { useCallback, useEffect, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { copyToClipboard } from '#src/app/shared/utils/clipboard';

const DEFAULT_RESET_MS = 3000;

type Options = {
	/** The text the share action copies, re-copied on presses during the confirmation window. */
	copyUrl: string;
	/** How long the confirmation lasts. */
	resetMs?: number;
};

type Result = {
	/**
	 * Changes on every successful copy (null once the confirmation resets), so `CopyLinkStatus` can re-key its
	 * content and screen readers announce repeat copies too.
	 */
	copyAnnouncementKey: number | null;
	/**
	 * Press handler for the copy button; `share` runs the copy and the share action's side effects. Presses while a
	 * copy is in flight are dropped, and presses during the confirmation window only re-copy `copyUrl` and
	 * re-announce it, so repeat presses don't re-run those side effects.
	 */
	press: (share: (() => void) | undefined) => void;
	afterShare: (success: boolean) => void;
};

export function useCopyLinkConfirmation({ copyUrl, resetMs = DEFAULT_RESET_MS }: Options): Result {
	const { i18n, errorReporter } = useUtilityContext();
	const [copyAnnouncementKey, setCopyAnnouncementKey] = useState<number | null>(null);
	const resetTimerRef = useRef<number | null>(null);
	// Expires on its own so a share action that never calls back can't leave the button unresponsive
	const copyStartedAtRef = useRef<number | null>(null);

	const confirmCopy = useCallback(() => {
		setCopyAnnouncementKey((key) => (key ?? 0) + 1);
		if (resetTimerRef.current != null) window.clearTimeout(resetTimerRef.current);
		resetTimerRef.current = window.setTimeout(() => {
			setCopyAnnouncementKey(null);
		}, resetMs);
	}, [resetMs]);

	const press = useCallback(
		(share: (() => void) | undefined) => {
			if (copyAnnouncementKey !== null) {
				// The clipboard may have changed since the first copy, so copy again before confirming it
				void (async () => {
					if (await copyToClipboard(copyUrl, i18n)) {
						confirmCopy();
					} else {
						void errorReporter.report(new Error('Could not copy link to clipboard'));
					}
				})();
				return;
			}
			const copyStartedAt = copyStartedAtRef.current;
			if (copyStartedAt !== null && Date.now() - copyStartedAt < resetMs) return;
			copyStartedAtRef.current = Date.now();
			share?.();
		},
		[copyAnnouncementKey, copyUrl, i18n, errorReporter, confirmCopy, resetMs],
	);

	const afterShare = useCallback(
		(success: boolean) => {
			copyStartedAtRef.current = null;
			if (success) confirmCopy();
		},
		[confirmCopy],
	);

	useEffect(
		() => () => {
			if (resetTimerRef.current != null) window.clearTimeout(resetTimerRef.current);
		},
		[],
	);

	return { copyAnnouncementKey, press, afterShare };
}
